/* eslint-disable max-len */
import {
	AfterViewInit,
	Component,
	ElementRef,
	OnDestroy,
	ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { ROUTES } from '@constants/routes';
import { USAA } from '@constants/usaa';
import { resetStores } from '@datorama/akita';
import { BrandConfig } from '@models/BrandConfig';
import { BreedService } from '@pages/pet-information/breed.service';
import { AnalyticsService } from '@services/analytics.service';
import { KameleoonService } from '@services/kameleoon.service';
import { LoggingService } from '@services/logging.service';
import { OrchestratorService } from '@services/orchestrator/orchestrator.service';
import { UtmService } from '@services/utm.service';

import { Observable, Subject, firstValueFrom } from 'rxjs';
import {
	distinctUntilChanged,
	filter,
	first,
	map,
	mergeMap,
	take,
	takeUntil,
} from 'rxjs/operators';

import { QuoteEngineCookieService } from '../app/quote-engine-cookie.service';
import { environment } from '../environments/environment';

import { UserTokenService } from './services/user-token.service';
import { ApplicationQuery } from './store/application/application.query';
import { ApplicationService } from './store/application/application.service';
import { BrandConfigQuery } from './store/brand/brand.query';
import { BrandConfigService } from './store/brand/brand.service';
import { BreedsService } from './store/breeds/breeds.service';
import { QuoteSessionQuery } from './store/session/session.query';
import { QuoteSessionService } from './store/session/session.service';
import { formatPhoneNumber } from './utils/phone-number-formatter';
import { WhitelabelService } from './whitelabel.service';

@Component({
	selector: 'qe-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
})
export class AppComponent implements AfterViewInit, OnDestroy {
	@ViewChild('epiLargeLoader')
	epiLargeLoader?: ElementRef<HTMLEpiLargeLoaderElement>;

	public readonly isProdEnvironment: boolean = environment.production;
	public readonly loadingText$: Observable<string>;
	public readonly loading$!: Observable<boolean>;
	public showDevRoutes: FormControl = new FormControl(false);
	public whitelabelLogoUrl: string = ``;
	public readonly ROUTES = ROUTES;
	public contactPhoneNumber: Observable<string | undefined> =
	this.brandGetters.phone$();
	public brandConfig$: Observable<BrandConfig>;
	public testName: string = 'none';
	public testVariation: string = 'not assigned';
	public kameleoonTestActive: boolean = false;
	public usaaSsoInactive: boolean = false;
	private styleLocation: string = '';
	private utmCampaignParam?: string | null = '';
	private readonly ngUnsubscribe$: Subject<boolean> = new Subject();

	constructor(
		private readonly loggingService: LoggingService,
		private readonly activatedRoute: ActivatedRoute,
		private readonly router: Router,
		private readonly appActions: ApplicationService,
		private readonly appQuery: ApplicationQuery,
		private readonly session: QuoteSessionService,
		private readonly sessionGetters: QuoteSessionQuery,
		private readonly whitelabel: WhitelabelService,
		private readonly gtmService: AnalyticsService,
		private readonly cookieService: QuoteEngineCookieService,
		private readonly utmService: UtmService,
		private readonly kameleoonService: KameleoonService,
		private readonly brandConfigActions: BrandConfigService,
		private readonly brandGetters: BrandConfigQuery,
		private readonly breed: BreedService,
		private readonly breedsState: BreedsService,
		private readonly auth: AuthService,
		private readonly userTokenService: UserTokenService,
		private readonly orchestratorService: OrchestratorService,
	) {
		const navigationEntries: PerformanceNavigationTiming[] = (<unknown>(
			performance.getEntriesByType('navigation')
		)) as PerformanceNavigationTiming[]; // eslint-disable-line
		const isLegacy: boolean = !navigationEntries.length;
		firstValueFrom(
			this.sessionGetters.sessionId$().pipe(map((id) => !!id))
		).then((hasSession) => {
			const pageRefreshed: boolean = isLegacy
				? performance.navigation.type === performance.navigation.TYPE_RELOAD
				: navigationEntries[0].type === 'reload';

			if (!pageRefreshed && hasSession) {
				this.session.reset();
			}
		});

		this.loadingText$ = this.appQuery.largeLoaderText$();
		this.brandConfig$ = this.brandGetters.brandConfig$();

		this.breed
			.getAllBreeds()
			.pipe(take(1))
			.subscribe((breeds) => {
				this.breedsState.update(breeds);
			});

		this.router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd),
				map(() => this.activatedRoute),
				map((route) => {
					while (route.firstChild) {
						route = route.firstChild;
					}

					return route;
				}),
				mergeMap((route) => route.data),
				takeUntil(this.ngUnsubscribe$)
			)
			.subscribe((data) => {
				this.appActions.update({ workflowStep: data?.step });
			});

		// Scroll to top on all route changes
		this.router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd)
			)
			.subscribe(() => {
				setTimeout(() => {
					scrollTo({
						top: 0,
						left: 0,
						behavior: `smooth`
					});
				}, 0);
			});

		this.sessionGetters
			.brand$()
			.pipe(
				filter((brand) => !!brand),
				map((brand) => ({ raw: brand, formatted: brand.toLowerCase() })),
				distinctUntilChanged(),
				takeUntil(this.ngUnsubscribe$)
				// eslint-disable-next-line @typescript-eslint/no-misused-promises
			)
			// eslint-disable-next-line  @typescript-eslint/no-misused-promises
			.subscribe(async (brand) => {
				const utmParams = this.utmService.getUtmCookie();

				this.appActions.setLoading(true);
				this.whitelabel
					.getPartnerData(brand.formatted, utmParams)
					.pipe(first())
					.subscribe((config) => {
						this.brandConfigActions.update(config);

						const formattedPhoneNumber = formatPhoneNumber(
							config.partnerPhoneNumber
						);

						this.styleLocation =
							config.whiteLabelConfig.quote.partnerAssets.styleSheetLocation;
						this.whitelabel.setWhitelabelStylesheet(
							brand.formatted,
							this.styleLocation
						);

						this.gtmService.push({
							event: `Quote Engine brand set`,
							// TODO: Remove this brand value after Google Analytics 4 changes are live
							brand: brand.raw,
							brandName: brand.formatted,
							brandPhoneNumber: config.partnerPhoneNumber,
							brandFormattedPhoneNumber: formattedPhoneNumber,
						});
					});

				if (brand.formatted === 'usaa') {
					const sessionId = await firstValueFrom(
						this.sessionGetters.sessionId$()
					);

					const urlParams = new URLSearchParams(window.location.search);
					this.utmCampaignParam = urlParams.get('utm_campaign');
					// if the state was already set in the guard via traits
					// OR we have a campaign utm saved in the cookie
					// OR utm_campaign is in the url
					this.usaaSsoInactive = this.appQuery.isUsaaSsoInactiveFlow$()
						|| utmParams.campaign === USAA.SSO_INACTIVE
						|| this.utmCampaignParam === USAA.SSO_INACTIVE;

					// set sso in app state for use later on contact info form
					this.appActions.setUsaaSsoInactive(this.usaaSsoInactive);

					// stop SSO path if it is a retrive or a non-sso USAA path
					if (this.isRetrieveUrl || this.usaaSsoInactive || !!sessionId) {
						this.appActions.setLoading(false);

						return;
					}
					this.auth.isLoading$
						.pipe(takeUntil(this.ngUnsubscribe$))
						.subscribe((loading) => {
							if (!loading) {
								this.auth.isAuthenticated$
									.pipe(takeUntil(this.ngUnsubscribe$))
									.subscribe({
										next: (isAthenticated) => {
											if (isAthenticated) {
												this.auth
													.getAccessTokenSilently({
														redirect_uri: environment?.USAAAuth?.callbackURL,
													})
													.pipe(first())
													.subscribe({
														next: (token) => {
															this.auth.user$
																.pipe(takeUntil(this.ngUnsubscribe$))
																.subscribe({
																	next: (user) => {
																		const memberId =
																			this.userTokenService.getMemberNumberFromToken(
																				user
																			);
																		if (memberId === null) return;
																		this.appActions.setLoading(
																			true,
																			undefined,
																			undefined,
																			this.constructor.name
																		);
																		this.session.updateContact({
																			organizationMemberIdentifier: memberId,
																		});
																		this.orchestratorService
																			.getShouldRedirect(memberId)
																			.pipe(first())
																			.subscribe({
																				next: (result) => {
																					if (!result) {
																						this.userTokenService.setUserData(
																							user
																						);
																						this.appActions.setLoading(
																							false,
																							undefined,
																							undefined,
																							this.constructor.name
																						);

																						return;
																					}
																					location.href =
																						environment?.USAAAuth?.USAARedirect;
																				},
																				error: (err) => {
																					this.appActions.setLoading(
																						false,
																						undefined,
																						undefined,
																						this.constructor.name
																					);
																					this.loggingService.error(err);
																				},
																			});
																	},
																	error: (err) =>
																		this.loggingService.error(err),
																});
														},

														error: (err) => this.loggingService.error(err),
													});

												return;
											}
											this.auth
												.loginWithRedirect({
													redirect_uri: environment.USAAAuth.callbackURL,
													connection: environment.USAAAuth.connection,
												})
												.pipe(first())
												.subscribe({
													next: () => {
														this.auth
															.getAccessTokenSilently({
																redirect_uri: environment.USAAAuth.callbackURL,
															})
															.pipe(first())
															.subscribe({
																next: (token) => {
																	this.userTokenService.setUserData(token);
																},
																error: (err) => {
																	this.loggingService.error(err);
																},
															});
													},
													error: (err) =>
														this.loggingService.error(
															'loginWithRedirect - Error:',
															err
														),
												});
										},
										error: (err) => this.loggingService.error(err),
									});
							}
						});
				} else {
					this.appActions.setUsaaSsoInactive(false);
				}
				this.appActions.setLoading(false);
			});
	}

	public get isRetrieveUrl(): boolean {
		return (
			!!location.search.length &&
			/email/i.test(location.search) &&
			/zip|zip_code|zipCode/.test(location.search)
		);
	}

	public get environmentId(): string {
		return environment.appEnvironment.toUpperCase();
	}

	/**
	 * For debugging
	 */
	public resetSession(): void {
		this.cookieService.clear();
		resetStores();
		// !!localStorage && localStorage.removeItem(environment.akitaPersistKey);
		!!localStorage && localStorage.clear();
		// !!sessionStorage && sessionStorage.removeItem(environment.akitaPersistKey);
		!!sessionStorage && sessionStorage.clear();
		window.location.reload();
	}

	ngAfterViewInit(): void {
		this.appQuery
			.loading$()
			.pipe(takeUntil(this.ngUnsubscribe$))
			.subscribe((loading) => {
				// eslint-disable-next-line @typescript-eslint/no-unused-expressions
				loading
					? this.epiLargeLoader?.nativeElement.show()
					: this.epiLargeLoader?.nativeElement.hide();
			});
	}

	ngOnDestroy(): void {
		this.ngUnsubscribe$.next(true);
		this.ngUnsubscribe$.unsubscribe();
	}

	public getActiveTest(): string {
		const { activeTest } = this.kameleoonService.getActiveTestDetails();

		return activeTest;
	}

	public getActiveTestVariation(): string {
		const { variation } = this.kameleoonService.getActiveTestDetails();

		return variation ? variation : 'not_targeted';
	}
}
